import React, { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { CheckCircle, Clock, AlertCircle, Eye } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'

interface CompactOperationIndicatorProps {
  operationId: string | null
  onOpenDialog: () => void
}

interface OperationStatus {
  id: string
  operation_type: string
  total_selected: number
  successful_count: number
  failed_count: number
  status: 'running' | 'completed' | 'failed'
  started_at: string
  completed_at?: string
}

export function CompactOperationIndicator({ 
  operationId, 
  onOpenDialog 
}: CompactOperationIndicatorProps) {
  const [operation, setOperation] = useState<OperationStatus | null>(null)
  const [progress, setProgress] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!operationId) {
      setIsVisible(false)
      return
    }

    setIsVisible(true)

    const pollOperation = async () => {
      try {
        const { data, error } = await supabase
          .from('mailbox_bulk_operations')
          .select('*')
          .eq('id', operationId)
          .single()

        if (error) throw error

        const operationData: OperationStatus = {
          ...data,
          status: data.completed_at ? 
            (data.failed_count > 0 && data.successful_count === 0 ? 'failed' : 'completed') :
            'running'
        }

        setOperation(operationData)

        // Calculate progress
        const total = operationData.total_selected
        const processed = Math.min(operationData.successful_count + operationData.failed_count, total)
        const progressPercent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0
        setProgress(progressPercent)

        // Show completion notification
        if (operationData.status === 'completed' && operation?.status === 'running') {
          toast.success(`Operation completed! ${operationData.successful_count} items processed successfully`)
        }
        
        if (operationData.status === 'failed' && operation?.status === 'running') {
          toast.error(`Operation failed! ${operationData.failed_count} items failed`)
        }

      } catch (error) {
        console.error('Error polling operation:', error)
      }
    }

    // Poll immediately, then every 2 seconds
    pollOperation()
    const interval = setInterval(pollOperation, 2000)
    
    return () => clearInterval(interval)
  }, [operationId, operation?.status])

  // Set up real-time updates
  useEffect(() => {
    if (!operationId) return

    const channel = supabase
      .channel('operation-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'mailbox_bulk_operations',
          filter: `id=eq.${operationId}`
        },
        (payload) => {
          const data = payload.new as any
          const operationData: OperationStatus = {
            ...data,
            status: data.completed_at ? 
              (data.failed_count > 0 && data.successful_count === 0 ? 'failed' : 'completed') :
              'running'
          }

          setOperation(operationData)
          
          const total = operationData.total_selected
          const processed = Math.min(operationData.successful_count + operationData.failed_count, total)
          const progressPercent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0
          setProgress(progressPercent)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [operationId])

  if (!isVisible || !operation) return null

  const getIcon = () => {
    switch (operation.status) {
      case 'running':
        return <Clock className="w-4 h-4 animate-spin" />
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      default:
        return <Clock className="w-4 h-4" />
    }
  }

  const getOperationLabel = () => {
    const typeMap: { [key: string]: string } = {
      'bulk_deactivate': 'Deactivating',
      'warmup_enable_toggle': 'Enabling Warmup',
      'warmup_disable_toggle': 'Disabling Warmup',
      'client_assignment': 'Assigning Client',
      'provider_update': 'Updating Provider',
      'bulk_delete': 'Deleting'
    }
    
    return typeMap[operation.operation_type] || 'Processing'
  }

  const getStatusColor = () => {
    switch (operation.status) {
      case 'running':
        return 'bg-blue-500'
      case 'completed':
        return 'bg-green-500'
      case 'failed':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-background border rounded-lg shadow-lg p-3 max-w-sm">
      <div className="flex items-center gap-3">
        {/* Status Icon */}
        <div className="flex-shrink-0">
          {getIcon()}
        </div>

        {/* Progress Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-sm font-medium truncate">
              {getOperationLabel()} Mailboxes
            </span>
            <Badge 
              variant="secondary" 
              className={`text-xs text-white ${getStatusColor()}`}
            >
              {operation.status === 'running' ? `${progress}%` : operation.status}
            </Badge>
          </div>
          
          {operation.status === 'running' && (
            <Progress value={progress} className="h-2 mb-2" />
          )}
          
          <div className="text-xs text-muted-foreground">
            {operation.status === 'running' ? (
              `${operation.successful_count + operation.failed_count} of ${operation.total_selected} processed`
            ) : operation.status === 'completed' ? (
              `✓ ${operation.successful_count} completed, ${operation.failed_count} failed`
            ) : (
              `✗ ${operation.failed_count} failed`
            )}
          </div>
        </div>

        {/* View Details Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={onOpenDialog}
          className="flex-shrink-0 h-8 w-8 p-0"
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}